import cx from 'classnames';
import {evite} from 'evite';
import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useSelector} from 'react-redux';
import {v5} from 'uuid';
import {initializeAmazonBids} from '~/ads/amazon';
import {
  CATEGORY_KEY,
  GPT_LOCAL_STORAGE_KEY,
  GPT_UUID,
  LOCATION_KEY,
  PAGE_TYPE_KEY,
  REFRESH_KEY,
  SECONDS_TO_WAIT_AFTER_VIEWABILITY,
} from '~/ads/constants';
import {getAdConfig} from '~/ads/getAdConfig';
import {PlaceholderAd} from '~/common/fabric/GoogleAds/PlaceholderAd';
import {RemoveAdsButton} from '~/common/fabric/GoogleAds/RemoveAdsButton';
import {selectClassifiedCategory} from '~/common/fabric/reducers/eviteEvent/selectors';
import {isFabricWebview} from '~/common/utils/webview';
import {Link} from '~library/atoms/Link';
import {Picture} from '~library/atoms/Picture';
import {useIsLargishAndUp} from '~library/hooks/useMatchQuery';

import './GoogleAdBanner.sass';

export const GoogleAdBanner = ({
  index = '',
  configKey = 'topLeaderboard', // 'topLeaderboard', 'leaderboard', 'sidePanel', 'photoShare', 'photoShareGallery'
  showRemoveButton = false,
  isCreateEditPreview = false,
  isGuestPreview = false,
  removeOnClick = null,
  location = '',
  pageType = '',
}) => {
  const isSidePanel = configKey === 'sidePanel';
  const isLargishAndUp = useIsLargishAndUp();
  const isMounted = useRef(true);
  const adRef = useRef(null);
  const adSlotRef = useRef(null);
  const refresh = useRef(false);
  const inFrame = useRef(false);
  const readyToListen = useRef(false);
  const adConfig = useMemo(
    () => getAdConfig({index, isLargishAndUp, configKey, location}),
    [index, isLargishAndUp, configKey, location]
  );
  const [adOverride, setAdOverride] = useState(null);
  const shouldShowRemoveButton =
    (showRemoveButton &&
      !isGuestPreview &&
      !((isFabricWebview() || window.is_webview) && !isGuestPreview)) ||
    false;
  const isPhotoShareAd = ['photoShareGallery', 'photoShare'].includes(configKey);
  const useFakeAds = isPhotoShareAd ? false : !!(isGuestPreview || isCreateEditPreview);
  const [unHideRemoveButton, setUnHideRemoveButton] = useState(useFakeAds && !isGuestPreview);

  const classifiedCategory = useSelector(selectClassifiedCategory);

  const {userId: loggedInUserId, guestId, hasUserOptedOut} = useSelector((state) => state?.user);

  const getHashedPPID = useCallback(() => {
    const GPTValue = localStorage.getItem(GPT_LOCAL_STORAGE_KEY);
    const anonId = guestId || crypto.randomUUID();
    if (!(loggedInUserId || GPTValue)) {
      localStorage.setItem(GPT_LOCAL_STORAGE_KEY, v5(anonId, GPT_UUID));
    }
    const uid = (loggedInUserId || GPTValue) ?? anonId;
    return v5(uid, GPT_UUID);
  }, [guestId, loggedInUserId]);

  const hashedPPID = !hasUserOptedOut ? getHashedPPID() : '';
  window.googletag?.pubads?.()?.setPublisherProvidedId(hashedPPID);

  const initializeAd = useCallback(() => {
    const elem = document.getElementById(adConfig.GoogleBannerAdIdWithIndex);
    if (adRef.current && adRef.current.id === adConfig.GoogleBannerAdIdWithIndex && elem) {
      window.googletag?.cmd.push(() => {
        window.apstag?.setDisplayBids();
        window.googletag?.display(adConfig.GoogleBannerAdIdWithIndex); // Ensure the div exists before calling display
        evite.viewability?.trackAd(adConfig.GoogleBannerAdIdWithIndex);
        window.googletag
          ?.pubads?.()
          ?.refresh([window.invitationPhotoAd], {changeCorrelator: false});
      });
    }
  }, [adConfig.GoogleBannerAdIdWithIndex]);

  const initializeGoogleAdSlots = useCallback(
    (adConfig) => {
      if (!isMounted.current || adSlotRef.current) {
        // Don't initialize if unmounted or slot already exists
        return;
      }
      const shouldRefresh = evite.features.isEnabled('auto_refresh_ads', false);

      window.googletag?.cmd?.push(() => {
        const adSlot = window.googletag.defineSlot(
          adConfig.adSlotUrl,
          adConfig.dimensions,
          adConfig.GoogleBannerAdIdWithIndex
        );

        if (adSlot) {
          adSlot.setTargeting(REFRESH_KEY, '0');
          adSlot.setTargeting(LOCATION_KEY, adConfig.location);
          adSlot.setTargeting(CATEGORY_KEY, classifiedCategory);
          if (pageType) adSlot.setTargeting(PAGE_TYPE_KEY, pageType);
          adSlot.addService(window.googletag?.pubads?.());
          adSlotRef.current = adSlot;
          window.invitationPhotoAd = adSlot;

          // Enable lazy loading
          window.googletag?.setConfig({
            lazyLoad: {
              fetchMarginPercent: 500, // Fetch ads when they are within 500% of the viewport
              renderMarginPercent: 200, // Render ads when they are within 200% of the viewport
              mobileScaling: 2.0, // Scale margins for mobile devices
            },
          });

          window.apstag?.setDisplayBids();

          // Initial timeout, so that all ads on page are evaluated on page load
          setTimeout(() => {
            if (isMounted.current && inFrame.current) {
              window.googletag?.pubads?.()?.refresh([adSlot]);
              adSlot.setTargeting(REFRESH_KEY, '1');
            } else {
              refresh.current = true;
            }
            readyToListen.current = true;
          }, SECONDS_TO_WAIT_AFTER_VIEWABILITY * 1000);
        }

        window.googletag?.pubads?.()?.addEventListener('slotResponseReceived', (event) => {
          const {slot} = event;
          if (!slot) return;

          if (showRemoveButton) setUnHideRemoveButton(true);
        });

        window.googletag?.pubads?.()?.addEventListener('impressionViewable', (event) => {
          if (event.slot.getAdUnitPath() !== adConfig.adSlotUrl) return;
          const {slot} = event;
          inFrame.current = true;
          if (!readyToListen.current) return;
          if (slot && shouldRefresh) {
            setTimeout(() => {
              if (isMounted.current && inFrame.current) {
                window.googletag?.pubads?.()?.refresh([slot]);
                slot.setTargeting(REFRESH_KEY, '1');
              } else {
                refresh.current = true;
              }
            }, SECONDS_TO_WAIT_AFTER_VIEWABILITY * 1000);
          }
        });

        window.googletag?.pubads?.()?.addEventListener('slotVisibilityChanged', (event) => {
          if (event.slot.getAdUnitPath() !== adConfig.adSlotUrl) return;
          const {slot, inViewPercentage} = event;
          if (shouldRefresh) {
            inFrame.current = inViewPercentage >= 50;
            if (inViewPercentage >= 50 && refresh.current) {
              refresh.current = false;
              window.googletag?.pubads?.()?.refresh([slot]);
              slot.setTargeting(REFRESH_KEY, '1');
            }
          }
        });
        initializeAd();
      });
    },
    [classifiedCategory, initializeAd, pageType]
  );

  const initializeAdOverride = useCallback(async () => {
    if (window.ad_override?.image) {
      setAdOverride(
        <Link
          className="fabric-free-banner"
          data-qa-id="google-ad-override-link"
          href={window.ad_override.link}
          as="transparent"
          target="_blank"
        >
          <Picture data-qa-id="google-ad-override-image" srcSet={window.ad_override.image} />
        </Link>
      );
    } else if (window.invitationPhotoAd && !isPhotoShareAd) {
      window.googletag?.cmd?.push(() => {
        window.googletag?.pubads?.()?.refresh([window.invitationPhotoAd]);
      });
    } else await initializeAmazonBids({index, isLargishAndUp, configKey, location});
    initializeGoogleAdSlots(adConfig);
  }, [
    adConfig,
    index,
    configKey,
    location,
    initializeGoogleAdSlots,
    isLargishAndUp,
    isPhotoShareAd,
  ]);

  useEffect(() => {
    initializeAdOverride();
  }, [initializeAdOverride]);

  useEffect(() => {
    if (!adOverride) {
      window.googletag?.cmd?.push(() => {
        window.googletag?.pubads?.()?.refresh([window.slideshowSideAd]);
      });
    }
  }, [adOverride]);

  useEffect(() => {
    isMounted.current = true;

    return () => {
      isMounted.current = false;
      if (adSlotRef.current) {
        window.googletag?.destroySlots([adSlotRef.current]);
        adSlotRef.current = null;
      }
    };
  }, []);

  return (
    <div
      className={cx('google-free-banner', {
        'google-free-banner--leaderboard': configKey === 'topLeaderboard',
        'google-free-banner--leaderboard-preview': isCreateEditPreview,
      })}
    >
      <div className="google-free-banner__wrapper-fit">
        {shouldShowRemoveButton && unHideRemoveButton && (
          <RemoveAdsButton
            sidePanel={isSidePanel}
            removeOnClick={removeOnClick}
            useFakeAds={useFakeAds}
          />
        )}
        {useFakeAds
          ? <PlaceholderAd sidePanel={isSidePanel} hideRealAds />
          : <div id={adConfig.GoogleBannerAdIdWithIndex} ref={adRef} className="gpt-banner">
              <PlaceholderAd spacerOnly sidePanel={isSidePanel} />
            </div>}
      </div>
    </div>
  );
};
